import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiMapPin, FiUsers, FiPlus, FiSearch, FiExternalLink, FiBarChart2 } from 'react-icons/fi';
import EventService from '../services/EventService';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await EventService.getAllPartnerEvents().catch(() => null);
        const list = res?.events || res?.data || res || [];
        setEvents(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error fetching partner events:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const filtered = events.filter(ev => {
    const matchesSearch = (ev.title || '').toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'ALL' || ev.eventType === filterType;
    return matchesSearch && matchesType;
  });

  const formatDateStr = (dateVal) => {
    if (!dateVal) return 'Upcoming';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return String(dateVal).split('T')[0];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    } catch {
      return String(dateVal).split('T')[0];
    }
  };

  const getPriceDisplay = (ev) => {
    if (ev.ticketTiers && Array.isArray(ev.ticketTiers) && ev.ticketTiers.length > 0) {
      const prices = ev.ticketTiers.map(t => Number(t.price || t.feeAmount || 0));
      const minP = Math.min(...prices);
      const maxP = Math.max(...prices);
      if (minP === 0 && maxP === 0) return 'Free Entry';
      if (minP === maxP) return minP === 0 ? 'Free Entry' : `₹${minP}`;
      if (minP === 0) return `Free - ₹${maxP}`;
      return `₹${minP} - ₹${maxP}`;
    }
    const p = Number(ev.ticketPrice || 0);
    return p > 0 ? `₹${p}` : 'Free Entry';
  };

  return (
    <div className="space-y-6 text-left pb-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <FiCalendar className="text-purple-700" /> Events Management
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Manage physical venue events, hybrid sessions, tickets & registrations</p>
        </div>
        <Link
          to="/app/create"
          className="px-4 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2 self-start md:self-auto"
        >
          <FiPlus className="w-4 h-4" /> + Create Event
        </Link>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
        <div className="relative w-full md:w-80">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events by title..."
            className="w-full bg-white border border-slate-300 rounded-xl py-2 pl-9 pr-4 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 font-medium"
          />
          <FiSearch className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          {['ALL', 'IRL', 'Hybrid', 'Online'].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                filterType === t ? 'bg-purple-700 text-white shadow-xs' : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="p-12 text-center text-slate-500 text-xs font-bold">Loading partner events...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((ev) => {
            const evId = ev._id || ev.eventId;
            const startDateFormatted = formatDateStr(ev.startDate);
            const priceText = getPriceDisplay(ev);
            const posterImg = ev.poster || ev.banner;

            const regCount = ev.interestedCount || ev.goingCount || 0;
            const tiersList = ev.ticketTiers || ev.ticketTypes || ev.tiers || [];
            const tierCapacitySum = Array.isArray(tiersList) && tiersList.length > 0
              ? tiersList.reduce((acc, t) => acc + Number(t.quantity || t.capacity || t.maxCapacity || 0), 0)
              : 0;
            const totalCap = tierCapacitySum > 0 ? tierCapacitySum : (ev.maxCapacity || ev.capacity || 200);

            return (
              <div key={evId} className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col justify-between overflow-hidden hover:border-purple-300 transition-all">
                <div className="h-40 w-full relative bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 overflow-hidden">
                  {posterImg ? (
                    <img
                      src={posterImg}
                      alt={ev.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border shadow-xs ${
                      ev.eventType === 'IRL' ? 'bg-emerald-600 text-white border-emerald-500' : ev.eventType === 'Hybrid' ? 'bg-purple-600 text-white border-purple-500' : 'bg-blue-600 text-white border-blue-500'
                    }`}>
                      {ev.eventType === 'IRL' ? '📍 IRL / Offline' : ev.eventType === 'Hybrid' ? '👥 Hybrid' : '🌐 Online'}
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-3 flex-1 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-500 font-bold flex items-center gap-1">
                        📅 {startDateFormatted}
                      </span>
                      {ev.startTime && (
                        <span className="text-[10px] text-slate-400 font-medium">⏰ {ev.startTime}</span>
                      )}
                    </div>

                    <h3 className="text-base font-bold text-slate-900 leading-tight">{ev.title}</h3>

                    <div className="text-xs text-slate-600 font-medium space-y-1">
                      <div className="flex items-center gap-1.5">
                        <FiMapPin className="text-purple-700 shrink-0" />
                        <span className="truncate">{ev.venueName || ev.venueAddress || 'Venue Details Provided'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FiUsers className="text-purple-700 shrink-0" />
                        <span>{regCount} Registered / {totalCap} Total Capacity</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs mt-3">
                    <div>
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Entry Pass</span>
                      <span className="font-black text-purple-900 text-sm">
                        {priceText}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link
                        to={`/app/events/${evId}/manage`}
                        className="px-3.5 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition-all flex items-center gap-1 shadow-xs"
                      >
                        <span>Manage</span>
                        <FiExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Events;
